/* ============================================================================
   IL | Brands left off the Brands page, kept off their products
   The Brands page's IL | Brand directory has a "Brands to leave out" list: suppliers Jo would
   rather not promote. A product page cannot read a section setting saved on another page, so
   the directory prints its list into its own markup (data-il-hidden-brands) and this script
   reads it from there, once per browser session.

   Turned on by Theme settings > IL | Brands. snippets/vendor.liquid then renders every brand
   name invisible (space kept, class il-brand-gate) and marked with data-il-brand-key. Once the
   list is known, a hidden brand's link is removed, which empties its product info block so
   Prestige's own :empty rule closes the gap, and every other brand is shown. The Notify me
   buttons carry the price alone in data-il-meta until their brand is cleared, then the full
   line from data-il-meta-full.

   Quick buy and other late content are caught by a MutationObserver, which runs before the
   browser paints, so a hidden brand is never seen. If the Brands page cannot be read, every
   brand shows, as Prestige ships.

   Brand names are held invisible only while <html> has il-brands-pending, which a one line
   inline script in layout/theme.liquid adds before the page paints, and ready() removes.

   Degrades safely: without JavaScript that class is never added, so brands show as before.
   See reference/THEME-EDITS.md.
   ========================================================================= */
(() => {
  const root = document.documentElement;
  const url = root.dataset.ilBrandsPage;
  if (!url) return;

  const SELECTOR = '[data-il-brand-key]';
  const CACHE_KEY = `il-hidden-brands:${url}`;
  let hidden = null;
  let loading = false;

  function apply(scope) {
    const found = [];
    if (scope.matches && scope.matches(SELECTOR)) found.push(scope);
    if (scope.querySelectorAll) found.push(...scope.querySelectorAll(SELECTOR));

    for (const el of found) {
      const isHidden = hidden.has(el.dataset.ilBrandKey);
      if (el.hasAttribute('data-il-meta-full')) {
        if (!isHidden) el.dataset.ilMeta = el.dataset.ilMetaFull;
        el.removeAttribute('data-il-brand-key');
      } else if (isHidden) {
        el.remove();
      } else {
        el.removeAttribute('data-il-brand-key');
      }
    }
  }

  function ready(list) {
    hidden = new Set(list);
    apply(document);
    root.classList.remove('il-brands-pending');
  }

  function parse(raw) {
    return (raw || '').split('|').filter(Boolean);
  }

  function load() {
    if (loading) return;
    loading = true;

    let cached = null;
    try { cached = sessionStorage.getItem(CACHE_KEY); } catch (e) { /* storage blocked */ }
    if (cached !== null) {
      ready(parse(cached));
      return;
    }

    fetch(url, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const holder = doc.querySelector('[data-il-hidden-brands]');
        const raw = holder ? holder.getAttribute('data-il-hidden-brands') : '';
        try { sessionStorage.setItem(CACHE_KEY, raw); } catch (e) { /* storage blocked */ }
        ready(parse(raw));
      })
      .catch(() => ready([]));
  }

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (hidden) {
          apply(node);
        } else if ((node.matches && node.matches(SELECTOR)) || (node.querySelector && node.querySelector(SELECTOR))) {
          load();
        }
      }
    }
  }).observe(root, { childList: true, subtree: true });

  if (document.querySelector(SELECTOR)) load();
})();
